import { useState } from 'react';
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TemplateContent } from '../../../../src/schemas/templateContent';
import { TemplateRenderer } from '@/components/TemplateRenderer';

interface LivePreviewPanelProps {
  templateData: {
    name: string;
    description: string;
    category_id: number;
    type_id: number;
    content: TemplateContent;
    owner_type: 'Organization';
    owner_org: number | null;
    owner_home: number | null;
    is_active: boolean;
    hidden_from: number[];
  };
  categories: any[];
  types?: any[];
}

export function LivePreviewPanel({ templateData, categories, types = [] }: LivePreviewPanelProps) {
  const [copied, setCopied] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const handleCopyJson = () => {
    const jsonString = JSON.stringify(templateData.content, null, 2);
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Recursively count all items including nested children
  const countAllItems = (items: any[]): number => {
    return items.reduce((total, item) => {
      const childCount = item.children && item.children.length > 0 
        ? countAllItems(item.children) 
        : 0;
      return total + 1 + childCount;
    }, 0);
  };

  const categoryName = categories.find((c: any) => c.id === templateData.category_id)?.name || 'Unknown';
  const typeName = types.find((t: any) => t.id === templateData.type_id)?.name || 'Unknown';
  const totalItemCount = countAllItems(templateData.content.items);

  return (
    <div className="lg:sticky lg:top-24 space-y-4">
      {/* Meta Fields Card */}
      <Card className="shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Template Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Name</p>
            <p className="text-sm font-medium break-words">{templateData.name || 'Untitled Template'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Category</p>
            <Badge variant="outline" className="text-xs">{categoryName}</Badge>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Type</p>
            <Badge variant="outline" className="text-xs">{typeName}</Badge>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Items</p>
            <p className="text-sm font-medium">
              {templateData.content.items.length} 
              {totalItemCount > templateData.content.items.length && (
                <span className="text-muted-foreground"> ({totalItemCount} total)</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Status</p>
            <Badge variant={templateData.is_active ? "default" : "secondary"} className="text-xs">
              {templateData.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Live Template Preview Card */}
      <Card className="shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Live Preview</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Read-only view of how users will see this template
          </p>
        </CardHeader>
        <CardContent>
          <TemplateRenderer content={templateData.content} readOnly={true} />
        </CardContent>
      </Card>

      {/* JSON Preview Card - Collapsible */}
      <Card className="shadow-lg">
        <CardHeader 
          className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg"
          onClick={() => setShowJson(!showJson)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <span>Template JSON</span>
              <Badge variant="outline" className="text-xs">Developer</Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              {showJson && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyJson();
                  }}
                  className="h-8 px-2"
                  data-testid="button-copy-json"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      <span className="text-xs">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      <span className="text-xs">Copy</span>
                    </>
                  )}
                </Button>
              )}
              {showJson ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </CardHeader>
        {showJson && (
          <CardContent>
            <div className="relative">
              <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto max-h-96 overflow-y-auto border border-border">
                <code>{JSON.stringify(templateData.content, null, 2)}</code>
              </pre>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
